import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

// ---- 类型 ----

export interface HourData {
  h: number;
  ghi: number | null;
  cloud: number | null;
  wind: number | null;
  precip: number | null;
}

export interface CityForecast {
  name: string;
  hours: (HourData | null)[];
}

export type VariableKey = 'ghi' | 'cloud' | 'wind' | 'precip';

export const VARIABLE_META: Record<VariableKey, { label: string; unit: string }> = {
  ghi: { label: '辐照', unit: 'W/m²' },
  cloud: { label: '云量', unit: '%' },
  wind: { label: '80m风速', unit: 'km/h' },
  precip: { label: '降水', unit: 'mm' },
};

export interface MapForecastResponse {
  date: string | null;
  model: string;
  data_type: string;
  updated_at: string | null;
  data_freshness: 'ok' | 'stale' | 'empty';
  data_freshness_note: string | null;
  cities: CityForecast[];
}

// ---- 地市常量 ----

export const PREFECTURES = [
  '济南市', '青岛市', '淄博市', '枣庄市', '东营市',
  '烟台市', '潍坊市', '济宁市', '泰安市', '威海市',
  '日照市', '临沂市', '德州市', '聊城市', '滨州市', '菏泽市',
] as const;

export type PrefectureName = (typeof PREFECTURES)[number];

/** GeoJSON 文件名映射 */
export const PREFECTURE_GEO: Record<PrefectureName, string> = {
  '济南市': 'jinan', '青岛市': 'qingdao', '淄博市': 'zibo',
  '枣庄市': 'zaozhuang', '东营市': 'dongying', '烟台市': 'yantai',
  '潍坊市': 'weifang', '济宁市': 'jining', '泰安市': 'taian',
  '威海市': 'weihai', '日照市': 'rizhao', '临沂市': 'linyi',
  '德州市': 'dezhou', '聊城市': 'liaocheng', '滨州市': 'binzhou',
  '菏泽市': 'heze',
};

// ---- 聚合工具函数 ----

/** 从 city_name（如 "潍坊市寿光市"）提取地市名 */
export function getPrefecture(cityName: string): PrefectureName | null {
  for (const p of PREFECTURES) {
    if (cityName.startsWith(p)) return p;
  }
  return null;
}

/** 某地市下辖县列表 */
export function getCounties(cities: CityForecast[], prefecture: PrefectureName): CityForecast[] {
  return cities.filter((c) => c.name.startsWith(prefecture));
}

/** 计算地市级聚合值（该市下辖各县的平均值） */
export function aggregatePrefecture(
  cities: CityForecast[],
  prefecture: PrefectureName,
  hour: number,
): { ghi: number | null; cloud: number | null; wind: number | null; precip: number | null } {
  const counties = getCounties(cities, prefecture);
  const vars = ['ghi', 'cloud', 'wind', 'precip'] as const;
  const result: Record<string, number | null> = {};

  for (const v of vars) {
    let sum = 0;
    let count = 0;
    for (const c of counties) {
      const h = c.hours[hour];
      if (h && h[v] !== null) {
        sum += h[v] as number;
        count++;
      }
    }
    result[v] = count > 0 ? sum / count : null;
  }

  return result as { ghi: number | null; cloud: number | null; wind: number | null; precip: number | null };
}

/** 计算某时刻全省所有县某个变量的最大值（每时动态上限） */
export function getProvinceMax(cities: CityForecast[], hour: number, variable: VariableKey): number {
  let max = 0;
  for (const c of cities) {
    const h = c.hours[hour];
    if (h && h[variable] !== null && h[variable]! > max) {
      max = h[variable]!;
    }
  }
  return max;
}

/** 计算全天 24 小时全省所有县某个变量的最大值（统一色阶标尺） */
export function getDayMax(cities: CityForecast[], variable: VariableKey): number {
  let max = 0;
  for (const c of cities) {
    for (const h of c.hours) {
      if (h && h[variable] !== null && h[variable]! > max) {
        max = h[variable]!;
      }
    }
  }
  return max;
}

// ---- Hook ----

export function useMapForecast() {
  return useQuery({
    queryKey: ['weather', 'map-forecast'],
    queryFn: async () => {
      const res = await apiClient.get<MapForecastResponse>('/weather/map-forecast');
      return res.data;
    },
    staleTime: 10 * 60 * 1000, // 10 分钟
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
